import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { times } from 'lodash';
import { useSet } from 'react-use';
import { useDebounce } from 'use-debounce';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { RawButton } from '../../../../../../../CommonComponents/OPL/Button/Button.styles';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { formatCurrency, formatNumber } from '../../../../../../../CommonUtils/formatNumber';
import { HtmlTable } from '../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { useTransactionFilters } from '../../../../../../hooks/useTransactionFilters';
import { useRouteParams } from '../../../../../../hooks/useRouteParams';
import { useGroupedTransactionsQuery } from '../../../../../../api/useGroupedTransactionsQuery';
import { PerformanceTableRow } from '../../../../../../components/PerformanceDetails/PerformanceDetails.styles';
import { Card } from '../../../../../../../CommonComponents/OPL/Card/Card';
import { ErrorState } from '../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { TextLoading } from '../../../../../../../CommonComponents/OPL/Text/Text.loading';
import { ColumnKey, COLUMNS, formatDate, SortBy, TOTAL_ROW_WIDTH } from './ActivityDetails.meta';
import { useFilteredData } from './hooks/useFilteredData';
import { useSortedData } from './hooks/useSortedData';
import { TruncatedText } from '../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { FixedCell } from './ActivityDetails.styles';
import { ActivityCsvExport } from '../../../../../../components/ActivityCsvExport/ActivityCsvExport';
import { useExportCsvButtonContainer } from '../../../../../../hooks/useExportCsvButtonContainer';
import { useFeatureFlag } from '../../../../../../../_hooks/useFeatureFlag';
import { Feature } from '../../../../../../../CommonSelectors/CommonArea.types';

export const ActivityDetails = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const exportCsvButtonContainer = useExportCsvButtonContainer();

  return (
    <>
      <Card>
        <Box display="flex" flexDirection="column" p="12">
          <Text variant="cardTitle" mb="12">
            Activity Detail
          </Text>

          <Input
            value={searchTerm}
            onChange={({ target: { value } }) => setSearchTerm(value)}
            placeholder="Search..."
            leftIcon="fa fa-search"
            aria-label="Search"
          />
        </Box>

        <HtmlTable variant="embedded" columnSpacing="small" maxHeight={600} fixedHead>
          <table>
            <TableContent searchTerm={debouncedSearchTerm} />
          </table>
        </HtmlTable>
      </Card>

      {!!exportCsvButtonContainer &&
        createPortal(<ActivityCsvExport tab="activity" />, exportCsvButtonContainer)}
    </>
  );
};

interface ContentProps {
  searchTerm: string;
}

const TableContent = React.memo(({ searchTerm }: ContentProps) => {
  const [sortBy, setSortBy] = useState<SortBy>({ column: 'name', sort: 'none' });

  const [expandedGroups, { has: isGroupExpanded, toggle: toggleGroupExpanded }] = useSet();

  const transactionFilters = useTransactionFilters();

  const { accountId, clientId, groupId } = useRouteParams();

  const { isLoading, isError, refetch, data: rawData } = useGroupedTransactionsQuery({
    clientId,
    accountId,
    groupId,
    period: transactionFilters.period.value,
    startDate: transactionFilters.period.dates[0],
    endDate: transactionFilters.period.dates[1],
  });

  const filteredData = useFilteredData({ data: rawData, filter: searchTerm });

  const data = useSortedData({ data: filteredData, sortBy });

  const showConfirmNotes = useFeatureFlag(Feature.ShowTransactionConfirmNotes);

  if (isLoading) {
    return (
      <tbody>
        {times(10).map(key => (
          <tr key={key}>
            {times(10).map(key2 => (
              <td key={key2}>
                <TextLoading height="body" width="100%" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }

  if (isError) {
    return (
      <tbody>
        <tr>
          <td>
            <ErrorState onAction={refetch} m="32" />
          </td>
        </tr>
      </tbody>
    );
  }

  if (!data) {
    return null;
  }

  const columns = (data.showTransactionDetail
    ? COLUMNS
    : COLUMNS.filter(col => !col.isDetail)
  ).filter(col => col.key !== 'confirmNotes' || showConfirmNotes);

  const columnCount = columns.length + 1;

  const sortIcon =
    sortBy.sort === 'asc'
      ? 'fas fa-arrow-up'
      : sortBy.sort === 'desc'
      ? 'fas fa-arrow-down'
      : undefined;

  const onSort = (column: ColumnKey) => {
    let newSort: SortBy['sort'] = 'none';

    if (sortBy?.column !== column) {
      newSort = 'asc';
    } else if (sortBy?.sort === 'none') {
      newSort = 'asc';
    } else if (sortBy.sort === 'asc') {
      newSort = 'desc';
    } else if (sortBy.sort === 'desc') {
      newSort = 'none';
    }

    setSortBy({
      column,
      sort: newSort,
    });
  };

  const showFixedColumnShadow = data.showTransactionDetail && expandedGroups.size > 0;

  return (
    <>
      <thead>
        <tr>
          <FixedCell as="th" showShadow={showFixedColumnShadow} style={{ zIndex: 2 }}>
            <RawButton
              onClick={() => onSort('groupName')}
              aria-label="Sort by column"
              css="width: 100%"
            >
              <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                <Text variant="tableHeader">Name</Text>

                {sortBy?.column === 'groupName' && (
                  <Icon className={sortIcon} color="neutral.500" />
                )}
              </Box>
            </RawButton>
          </FixedCell>

          {columns.map(col => (
            <th key={col.key}>
              <RawButton
                onClick={() => onSort(col.key)}
                aria-label="Sort by column"
                css="width: 100%"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={col.align === 'left' ? 'flex-start' : 'flex-end'}
                  css="gap: 4px"
                  p="2"
                >
                  <Text variant="tableHeader">{col.title}</Text>

                  {sortBy.column === col.key && <Icon className={sortIcon} color="neutral.500" />}
                </Box>
              </RawButton>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        <tr>
          <FixedCell showShadow={showFixedColumnShadow}>
            <TruncatedText width={TOTAL_ROW_WIDTH} fontWeight={600}>
              Beginning Market Value
            </TruncatedText>
          </FixedCell>

          {times(columnCount - 2).map(key => (
            <td key={key} />
          ))}

          <td>
            <Text fontWeight={600} variant="tabular" textAlign="right">
              {formatCurrency(data.beginningMarketValue, {
                decimalPlaces: 2,
              })}
            </Text>
          </td>
        </tr>

        {data.groupedTransactions.map(group => {
          const hasChildRows = group.transactions.length > 0;
          const isExpanded = isGroupExpanded(group.name);
          const tableRowVariant = hasChildRows ? 'clickable' : 'default';

          if (!hasChildRows) {
            return null;
          }

          return (
            <React.Fragment key={group.name}>
              <PerformanceTableRow
                isExpanded={isExpanded}
                onClick={hasChildRows ? () => toggleGroupExpanded(group.name) : undefined}
                variant={tableRowVariant}
              >
                <FixedCell showShadow={showFixedColumnShadow}>
                  <Box display="flex" alignItems="center">
                    {hasChildRows && (
                      <Icon
                        className={isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
                        color="neutral.400"
                        mr={isExpanded ? 8 : 12}
                      />
                    )}

                    <TruncatedText width={132} fontWeight={600}>
                      {group.name}
                    </TruncatedText>
                  </Box>
                </FixedCell>

                {times(columnCount - 2).map(key => (
                  <td key={key} />
                ))}

                <td>
                  <Text fontWeight={600} variant="tabular" textAlign="right">
                    {formatCurrency(group.total, {
                      decimalPlaces: 2,
                    })}
                  </Text>
                </td>
              </PerformanceTableRow>

              {isGroupExpanded(group.name) && (
                <>
                  {group.transactions.map(transaction => (
                    <PerformanceTableRow key={transaction.id}>
                      <FixedCell showShadow={showFixedColumnShadow} />

                      <td>
                        <Text width={85} fontWeight={500} textAlign="left">
                          {formatDate(transaction.date)}
                        </Text>
                      </td>

                      <td>
                        <TruncatedText width={130} fontWeight={500} textAlign="right">
                          {transaction.accountNumber}
                        </TruncatedText>
                      </td>

                      {data.showTransactionDetail && (
                        <Box as="td" display="flex" alignItems="center">
                          <TruncatedText
                            maxWidth={140}
                            fontWeight={500}
                            textAlign="left"
                            style={{ display: 'inline-block' }}
                          >
                            {transaction.description}
                          </TruncatedText>

                          {transaction.notes && (
                            <Tooltip content={transaction.notes} placement="top">
                              <Icon
                                className="far fa-info-circle"
                                color="neutral.500"
                                fontSize="small"
                                ml="4"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {showConfirmNotes && (
                        <td>
                          <Box maxWidth={160}>
                            <TruncatedText fontWeight={500}>
                              {transaction.confirmNotes || ''}
                            </TruncatedText>
                          </Box>
                        </td>
                      )}

                      <td>
                        <TruncatedText
                          width={190}
                          fontWeight={500}
                          variant="tabular"
                          textAlign="left"
                        >
                          {transaction.name}
                        </TruncatedText>
                      </td>

                      <td>
                        <TruncatedText
                          width={90}
                          fontWeight={500}
                          variant="tabular"
                          textAlign="left"
                        >
                          {transaction.ticker}
                        </TruncatedText>
                      </td>

                      {data.showTransactionDetail && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Text fontWeight={500} variant="tabular" textAlign="right">
                            {transaction.units &&
                              formatNumber(transaction.units, { decimalPlaces: 2 })}
                          </Text>
                        </td>
                      )}

                      {data.showTransactionDetail && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Text fontWeight={500} variant="tabular" textAlign="right">
                            {transaction.price &&
                              formatCurrency(transaction.price, { decimalPlaces: 2 })}
                          </Text>
                        </td>
                      )}

                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Text fontWeight={500} variant="tabular" textAlign="right">
                          {transaction.total &&
                            formatCurrency(transaction.total, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </PerformanceTableRow>
                  ))}
                </>
              )}
            </React.Fragment>
          );
        })}

        {data.accruedIncomeChange != null && (
          <PerformanceTableRow>
            <FixedCell showShadow={showFixedColumnShadow}>
              <TruncatedText width={TOTAL_ROW_WIDTH} fontWeight={600}>
                Change In Accrued Income
              </TruncatedText>
            </FixedCell>

            {times(columnCount - 2).map(key => (
              <td key={key} />
            ))}

            <td>
              <Text fontWeight={600} variant="tabular" textAlign="right">
                {formatCurrency(data.accruedIncomeChange, { decimalPlaces: 2 })}
              </Text>
            </td>
          </PerformanceTableRow>
        )}

        <PerformanceTableRow>
          <FixedCell showShadow={showFixedColumnShadow}>
            <TruncatedText width={TOTAL_ROW_WIDTH} fontWeight={600}>
              Market Value Change
            </TruncatedText>
          </FixedCell>

          {times(columnCount - 2).map(key => (
            <td key={key} />
          ))}

          <td>
            <Text fontWeight={600} variant="tabular" textAlign="right">
              {formatCurrency(data.marketValueChange, { decimalPlaces: 2 })}
            </Text>
          </td>
        </PerformanceTableRow>

        <PerformanceTableRow>
          <FixedCell showShadow={showFixedColumnShadow}>
            <TruncatedText width={TOTAL_ROW_WIDTH} fontWeight={600}>
              Ending Market Value
            </TruncatedText>
          </FixedCell>

          {times(columnCount - 2).map(key => (
            <td key={key} />
          ))}

          <td>
            <Text fontWeight={600} variant="tabular" textAlign="right">
              {formatCurrency(data.endingMarketValue, { decimalPlaces: 2 })}
            </Text>
          </td>
        </PerformanceTableRow>
      </tbody>
    </>
  );
});
